import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useLang } from "@/context/LangContext";
import {
  Sparkles,
  ShieldCheck,
  ArrowRight,
  ArrowLeft,
  Check,
  AlertTriangle,
  Wallet,
  ExternalLink,
  Loader2,
} from "lucide-react";

// Wizard step ids — small integers persisted to the backend via
// PATCH /api/pricing-profile/setup-progress. ``99`` is the completion
// sentinel set by POST /api/pricing-profile/mark-ready.
//
// Feb 2026 UX pass (Rio dry-run): a "Pricing style" step (id=2) is now
// asked BEFORE trades so the wizard doesn't feel labor-rate-first. The
// "Labor rate" step (id=6) is now visible only when the contractor picked
// a style that actually uses hourly labor (labor_materials / hybrid) and
// is hidden entirely for unit/production or flat/lump-sum contractors.
// The step-pill stepper and the "Step X of Y" chrome derive dynamically
// from ``visibleSteps`` so the numbers stay honest.
const STEPS = [
  { id: 1,  key: "welcome",       label: "Welcome" },
  { id: 2,  key: "pricing-style", label: "Pricing style" },
  { id: 3,  key: "trades",        label: "Trades" },
  { id: 4,  key: "systems",       label: "System types" },
  { id: 5,  key: "templates",     label: "Load templates" },
  { id: 6,  key: "labor",         label: "Labor rate" },
  { id: 7,  key: "markup",        label: "Markup" },
  { id: 8,  key: "tax-deposit",   label: "Tax & deposit" },
  { id: 9,  key: "review",        label: "Review" },
  { id: 10, key: "confirm",       label: "Confirm" },
];

// Contractor-declared default pricing style. Feb 2026 UX pass — the picker
// now expresses the FOUR Product Owner-approved contractor choices. Legacy
// values (``labor_materials``, ``hybrid``) still round-trip through PUT/GET
// but are surfaced under the "Other / Help Me Set It Up" advanced path.
const PRICING_STYLE_OPTIONS = [
  {
    id: "unit",
    title: "All-in Price by Unit",
    subtitle: "Recommended",
    desc: "One complete selling price per repeatable unit — materials, labor, overhead, and profit already included. Works for square, sq ft, linear ft, fixture, opening, item, and more.",
  },
  {
    id: "flat",
    title: "All-in Price by Job or System",
    desc: "One complete selling price per job, system, or defined assembly — materials, labor, overhead, and profit included. Common for HVAC and other system-based trades.",
  },
  {
    id: "learn_from_history",
    title: "Learn From My Approved Estimates",
    desc: "Levi reviews your approved past estimates and suggests pricing. You review and approve every price before it becomes a rule.",
  },
  {
    id: "help_me_setup",
    title: "Other / Help Me Set It Up",
    desc: "Not sure yet? Levi routes you into the existing advanced setup — labor + materials, T&M, flat, or a mix — with contractor control preserved throughout.",
  },
];
const DEFAULT_PRICING_STYLE = "unit";
const PREFERRED_UNIT_OPTIONS = [
  "Roofing square",
  "Sq ft",
  "Linear ft",
  "Fixture",
  "Opening",
  "Item",
  "Each",
  "Room",
  "Day",
  "Hour",
  "Flat / project",
  "Other",
];

// Styles that mean the contractor uses hourly labor on line items and
// therefore need the labor-rate step. Feb 2026: ``help_me_setup`` opts
// contractors into the existing advanced setup (labor + materials, T&M,
// hybrid), so the labor step is included for that path. Legacy values
// remain here for backwards compat.
const LABOR_STYLES = new Set([
  "labor_materials", "hybrid", "help_me_setup",
]);

// Legacy persisted values that predate the Feb 2026 four-choice UX. On
// load they map to the closest new-choice bucket for display only; we do
// NOT overwrite the persisted value until the contractor saves.
const LEGACY_STYLE_MAP = {
  labor_materials: "help_me_setup",
  hybrid: "help_me_setup",
};

const _normalizeStyleForPicker = (val) =>
  LEGACY_STYLE_MAP[val] || val || DEFAULT_PRICING_STYLE;

const PRICING_TYPE_LABELS = {
  unit_priced: "Unit priced",
  fixed_price: "Fixed price",
  allowance: "Allowance",
  sub_quote: "Sub-quote",
  tm: "Time & materials (T&M)",
};

const WizardFooter = ({ onBack, onNext, nextLabel = "Continue", nextDisabled = false, nextTestId = "wizard-next-btn", backTestId = "wizard-back-btn", busy }) => (
  <div className="mt-8 flex items-center justify-between">
    <button
      onClick={onBack}
      disabled={!onBack || busy}
      data-testid={backTestId}
      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)] disabled:opacity-40"
    >
      <ArrowLeft size={14} /> Back
    </button>
    <button
      onClick={onNext}
      disabled={!onNext || nextDisabled || busy}
      data-testid={nextTestId}
      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60"
    >
      {busy ? <Loader2 className="animate-spin" size={14} /> : null}
      {nextLabel} <ArrowRight size={14} />
    </button>
  </div>
);

// ============================================================
// Phase 5B · Pricing Sources panel (always visible on Welcome step).
// Three PO-worded choices: build manually · import a pricing guide ·
// build pricing from previous estimates. The third option surfaces
// Levi's staged observations for contractor review INSIDE the wizard.
// Nothing here activates pricing — approval flows through existing
// wizard steps as usual.
// ============================================================
const PricingSourcesPanel = () => {
  const [selected, setSelected] = useState("manual");
  const [analysis, setAnalysis] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // Phase 5B · Inline "Review in Wizard" state. When a draft is picked,
  // this holds the draft being reviewed + the editable form snapshot.
  // Approval submits through the EXISTING wizard endpoints — never a
  // shortcut path. On success we transition the draft to "approved".
  const [reviewDraft, setReviewDraft] = useState(null);
  const [reviewForm, setReviewForm] = useState(null);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewError, setReviewError] = useState("");

  const loadAnalysis = async () => {
    setBusy(true);
    try {
      const [aRes, dRes] = await Promise.all([
        api.get("/api/pricing-profile/estimate-analysis"),
        api.get("/api/pricing-profile/estimate-analysis/drafts?status_filter=draft"),
      ]);
      setAnalysis(aRes.data);
      setDrafts(dRes.data?.drafts || []);
      setLoaded(true);
    } catch (e) {
      toast.error("Couldn't load estimate history right now.");
    } finally {
      setBusy(false);
    }
  };

  const stageObservation = async (obs) => {
    try {
      await api.post("/api/pricing-profile/estimate-analysis/stage", {
        trade: obs.trade,
        system_type: obs.system_type,
        unit: obs.unit,
        selling_price_per_unit: obs.max_price,
        source_estimate_ids: obs.source_estimate_ids || [],
        sample_line_names: obs.sample_line_names || [],
        observation_kind: obs.observation_kind || "suggested_rule_candidate",
      });
      toast.success("Staged for your review in the pricing wizard.");
      await loadAnalysis();
    } catch {
      toast.error("Couldn't stage that observation right now.");
    }
  };

  const transitionDraft = async (draftId, to, extras = {}) => {
    try {
      await api.post(
        `/api/pricing-profile/estimate-analysis/drafts/${draftId}/transition`,
        { to, ...extras },
      );
      await loadAnalysis();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't update that draft.";
      toast.error(typeof msg === "string" ? msg : "Couldn't update that draft.");
    }
  };

  // Phase 5B · Open the existing Wizard's system-config form inline,
  // hydrated from the staged draft. Only fields the analyzer evidence
  // supports are pre-filled. Everything else is left blank — the
  // contractor sees a clear "Required" flag and must supply it.
  const openReviewInWizard = (draft) => {
    setReviewError("");
    setReviewDraft(draft);
    setReviewForm({
      trade: draft.trade || "",
      system_type: draft.system_type || "",
      unit: draft.unit || "",
      selling_price_per_unit: draft.selling_price_per_unit || 0,
      // Approach defaults are set by the existing wizard evaluators;
      // Levi's evidence only supports installed-selling per unit today.
      pricing_method: "per_unit",
      pricing_structure: "complete_installed",
      // Fields the analyzer does NOT observe — leave blank; contractor
      // must fill in before Save & Approve when required.
      rule_name: "",
    });
  };

  const closeReview = () => {
    setReviewDraft(null);
    setReviewForm(null);
    setReviewError("");
  };

  const saveAndApproveReview = async () => {
    if (!reviewDraft || !reviewForm) return;
    setReviewBusy(true);
    setReviewError("");
    try {
      const { trade, system_type } = reviewForm;
      if (!trade || !system_type) {
        setReviewError("Trade and system are required.");
        setReviewBusy(false);
        return;
      }
      if (!reviewForm.unit) {
        setReviewError("Unit is required.");
        setReviewBusy(false);
        return;
      }
      if (!reviewForm.selling_price_per_unit
          || Number(reviewForm.selling_price_per_unit) <= 0) {
        setReviewError("Selling price per unit is required.");
        setReviewBusy(false);
        return;
      }
      // Step 1 — write the system meta via the EXISTING wizard endpoint.
      const encodedSystem = encodeURIComponent(system_type);
      const encodedTrade = encodeURIComponent(trade);
      await api.put(
        `/api/onboarding/trade-setup/trade/${encodedTrade}/system/${encodedSystem}`,
        {
          pricing_method: reviewForm.pricing_method,
          pricing_structure: reviewForm.pricing_structure,
          selling_price_per_unit: Number(reviewForm.selling_price_per_unit),
          selling_price_unit: reviewForm.unit,
          // For installed-selling systems the wizard's readiness rule
          // requires markup_handling=built_in — Levi's evidence-based
          // hydration implies "all-in price includes markup" by design.
          // Contractor may still switch method above; downstream wizard
          // steps remain fully editable.
          markup_handling: reviewForm.pricing_method === "per_unit"
            && reviewForm.pricing_structure === "complete_installed"
            ? "built_in" : "",
        },
      );
      // Step 2 — approve via the EXISTING wizard endpoint. Returns
      // meta + readiness; a successful approve writes the rule and
      // sets active_for_estimates=true.
      await api.post(
        `/api/onboarding/trade-setup/trade/${encodedTrade}/system/${encodedSystem}/approve`,
      );
      // Step 3 — compute the starter_key the server just wrote. The
      // server uses "slice2b1.{system_type}.installed" for per_unit +
      // complete_installed; other methods use the pricing_method as
      // the suffix. This mirrors trade_setup.py's build_rule.
      const suffix = (reviewForm.pricing_method === "per_unit"
                      && reviewForm.pricing_structure === "complete_installed")
        ? "installed"
        : reviewForm.pricing_method;
      const starterKey = `slice2b1.${system_type}.${suffix}`;
      // Step 4 — transition the draft to "approved". The server
      // verifies the rule exists and is active_for_estimates=true.
      // Client cannot fake this transition.
      await api.post(
        `/api/pricing-profile/estimate-analysis/drafts/${reviewDraft.id}/transition`,
        { to: "approved", activated_rule_starter_key: starterKey },
      );
      toast.success("Approved. This pricing is now active in your estimates.");
      closeReview();
      await loadAnalysis();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      let msg = "Couldn't approve this pricing right now.";
      if (typeof detail === "string") msg = detail;
      else if (detail && typeof detail === "object" && detail.code) {
        msg = `Couldn't approve: ${detail.code}. Please review the values and try again.`;
      }
      setReviewError(msg);
    } finally {
      setReviewBusy(false);
    }
  };

  const observations = analysis?.observations || [];
  const candidateCount = observations.filter(
    (o) => o.observation_kind === "suggested_rule_candidate" && !o.already_in_profile,
  ).length;

  return (
    <section
      className="jc-card p-6 mt-6"
      data-testid="wizard-sources-panel"
    >
      <div className="text-sm font-semibold">How would you like to build your pricing?</div>
      <div className="text-xs text-[var(--jc-text-soft)] mt-1">
        Choose any starting point. You always review and approve every price inside this wizard before it becomes active.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
        <button
          data-testid="wizard-sources-option-manual"
          type="button"
          className={`text-left rounded-md border p-3 hover:border-[var(--jc-blue)] ${selected === "manual" ? "border-[var(--jc-blue)] bg-[var(--jc-blue)]/5" : "border-[var(--jc-border)]"}`}
          onClick={() => setSelected("manual")}
        >
          <div className="text-sm font-medium">Build pricing manually</div>
          <div className="text-xs text-[var(--jc-text-soft)] mt-1">Walk through this wizard step by step and enter your numbers.</div>
        </button>
        <button
          data-testid="wizard-sources-option-import"
          type="button"
          className={`text-left rounded-md border p-3 hover:border-[var(--jc-blue)] ${selected === "import" ? "border-[var(--jc-blue)] bg-[var(--jc-blue)]/5" : "border-[var(--jc-border)]"}`}
          onClick={() => setSelected("import")}
        >
          <div className="text-sm font-medium">Import an existing pricing guide</div>
          <div className="text-xs text-[var(--jc-text-soft)] mt-1">Upload a pricing workbook. Every row stays as a draft until you approve it.</div>
        </button>
        <button
          data-testid="wizard-sources-option-previous"
          type="button"
          className={`text-left rounded-md border p-3 hover:border-[var(--jc-blue)] ${selected === "previous" ? "border-[var(--jc-blue)] bg-[var(--jc-blue)]/5" : "border-[var(--jc-border)]"}`}
          onClick={() => { setSelected("previous"); if (!loaded) loadAnalysis(); }}
        >
          <div className="text-sm font-medium flex items-center gap-2">
            Build pricing from previous estimates
            {candidateCount > 0 && (
              <span
                data-testid="wizard-sources-candidate-badge"
                className="text-[10px] font-semibold rounded-full bg-[var(--jc-blue)] text-white px-2 py-0.5"
              >
                {candidateCount}
              </span>
            )}
          </div>
          <div className="text-xs text-[var(--jc-text-soft)] mt-1">Levi reviews your completed estimates and shows you the patterns. You review and approve every price.</div>
        </button>
      </div>

      {selected === "previous" && (
        <div className="mt-5 space-y-4" data-testid="wizard-sources-previous-panel">
          {busy && <div className="text-xs text-[var(--jc-text-soft)]">Reading your completed estimates…</div>}
          {!busy && loaded && observations.length === 0 && (
            <div className="text-xs text-[var(--jc-text-soft)]" data-testid="wizard-sources-empty">
              No pricing patterns are ready for review yet. As you complete more estimates, Levi can help identify repeated pricing.
            </div>
          )}
          {!busy && observations.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-[var(--jc-text-soft)]">Observations</div>
              {observations.map((o, i) => (
                <div
                  key={`${o.trade}-${o.system_type}-${o.unit}-${i}`}
                  data-testid="wizard-sources-observation-row"
                  className="rounded-md border border-[var(--jc-border)] p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                >
                  <div className="text-sm">
                    <div className="font-medium">{o.trade} · {o.system_type}</div>
                    <div className="text-xs text-[var(--jc-text-soft)] mt-0.5">
                      {o.sample_count} completed {o.sample_count === 1 ? "estimate" : "estimates"} · {" "}
                      {o.min_price === o.max_price
                        ? <>${o.min_price} per {o.unit}</>
                        : <>between ${o.min_price} and ${o.max_price} per {o.unit}</>}
                    </div>
                    {(o.sample_line_names || []).length > 0 && (
                      <div className="text-[11px] text-[var(--jc-text-soft)] mt-0.5">
                        e.g. {(o.sample_line_names || []).slice(0, 3).join(" · ")}
                      </div>
                    )}
                    {o.already_in_profile && (
                      <div className="text-[11px] font-medium text-[var(--jc-green,#10b981)] mt-0.5">Already in your pricing</div>
                    )}
                  </div>
                  {!o.already_in_profile && (
                    <div className="flex gap-2 shrink-0">
                      <button
                        data-testid="wizard-sources-stage-btn"
                        type="button"
                        onClick={() => stageObservation(o)}
                        className="text-xs rounded-md bg-[var(--jc-blue)] text-white px-3 py-1.5 hover:opacity-90"
                      >
                        Stage for review
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {drafts.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-[var(--jc-border)]">
              <div className="text-xs font-semibold uppercase tracking-wide text-[var(--jc-text-soft)]">Ready for your review</div>
              {drafts.map((d) => (
                <div
                  key={d.id}
                  data-testid="wizard-sources-draft-row"
                  className="rounded-md border border-[var(--jc-border)] p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                >
                  <div className="text-sm">
                    <div className="font-medium">{d.trade} · {d.system_type}</div>
                    <div className="text-xs text-[var(--jc-text-soft)]">${d.selling_price_per_unit} per {d.unit} · from {(d.source_estimate_ids || []).length} completed estimates</div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      data-testid="wizard-sources-draft-review"
                      type="button"
                      onClick={() => openReviewInWizard(d)}
                      className="text-xs rounded-md bg-[var(--jc-blue)] text-white px-3 py-1.5 hover:opacity-90"
                    >
                      Review in Wizard
                    </button>
                    <button
                      data-testid="wizard-sources-draft-defer"
                      type="button"
                      onClick={() => transitionDraft(d.id, "deferred")}
                      className="text-xs rounded-md border border-[var(--jc-border)] px-3 py-1.5 hover:bg-[var(--jc-bg-soft,#f7f7f8)]"
                    >
                      Defer
                    </button>
                    <button
                      data-testid="wizard-sources-draft-dismiss"
                      type="button"
                      onClick={() => transitionDraft(d.id, "dismissed")}
                      className="text-xs rounded-md border border-[var(--jc-border)] px-3 py-1.5 hover:bg-[var(--jc-bg-soft,#f7f7f8)]"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              ))}
              <div className="text-[11px] text-[var(--jc-text-soft)]">
                Continue the wizard steps to confirm and activate any staged pricing. Nothing here is active until you approve it in the pricing wizard.
              </div>
            </div>
          )}

          {reviewDraft && reviewForm && (
            <div
              className="rounded-md border border-[var(--jc-blue)] bg-[var(--jc-blue)]/5 p-4 mt-4"
              data-testid="wizard-sources-review-form"
            >
              <div className="text-sm font-semibold">Review in Wizard · {reviewDraft.trade} · {reviewDraft.system_type}</div>
              <div className="text-xs text-[var(--jc-text-soft)] mt-1">
                Confirm every value. Nothing becomes active until you save and approve this pricing here. Levi never approves pricing on your behalf.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <label className="text-xs text-[var(--jc-text)] flex flex-col">
                  <span className="font-medium">Trade</span>
                  <input
                    data-testid="wizard-sources-review-trade"
                    className="jc-input mt-1"
                    value={reviewForm.trade}
                    onChange={(e) => setReviewForm((f) => ({ ...f, trade: e.target.value }))}
                  />
                </label>
                <label className="text-xs text-[var(--jc-text)] flex flex-col">
                  <span className="font-medium">System</span>
                  <input
                    data-testid="wizard-sources-review-system"
                    className="jc-input mt-1"
                    value={reviewForm.system_type}
                    onChange={(e) => setReviewForm((f) => ({ ...f, system_type: e.target.value }))}
                  />
                </label>
                <label className="text-xs text-[var(--jc-text)] flex flex-col">
                  <span className="font-medium">Item / rule name (optional)</span>
                  <input
                    data-testid="wizard-sources-review-name"
                    className="jc-input mt-1"
                    placeholder="Leave blank to use the system name"
                    value={reviewForm.rule_name}
                    onChange={(e) => setReviewForm((f) => ({ ...f, rule_name: e.target.value }))}
                  />
                </label>
                <label className="text-xs text-[var(--jc-text)] flex flex-col">
                  <span className="font-medium">Unit</span>
                  <input
                    data-testid="wizard-sources-review-unit"
                    className="jc-input mt-1"
                    value={reviewForm.unit}
                    onChange={(e) => setReviewForm((f) => ({ ...f, unit: e.target.value }))}
                  />
                </label>
                <label className="text-xs text-[var(--jc-text)] flex flex-col">
                  <span className="font-medium">Selling price per unit</span>
                  <input
                    data-testid="wizard-sources-review-price"
                    className="jc-input mt-1"
                    type="number"
                    step="0.01"
                    min="0"
                    value={reviewForm.selling_price_per_unit}
                    onChange={(e) => setReviewForm((f) => ({ ...f, selling_price_per_unit: e.target.value }))}
                  />
                </label>
                <label className="text-xs text-[var(--jc-text)] flex flex-col">
                  <span className="font-medium">Pricing method</span>
                  <select
                    data-testid="wizard-sources-review-method"
                    className="jc-input mt-1"
                    value={reviewForm.pricing_method}
                    onChange={(e) => setReviewForm((f) => ({ ...f, pricing_method: e.target.value }))}
                  >
                    <option value="per_unit">All-in price per unit</option>
                    <option value="flat">All-in flat price</option>
                    <option value="materials_labor">Materials + Labor</option>
                  </select>
                </label>
              </div>
              <div className="text-[11px] text-[var(--jc-text-soft)] mt-2">
                Provenance: from {(reviewDraft.source_estimate_ids || []).length} completed estimates.
                {(reviewDraft.sample_line_names || []).length > 0 && (
                  <> · e.g. {(reviewDraft.sample_line_names || []).slice(0, 3).join(" · ")}</>
                )}
              </div>
              {reviewError && (
                <div
                  data-testid="wizard-sources-review-error"
                  className="mt-3 text-xs text-[var(--jc-red,#dc2626)]"
                >
                  {reviewError}
                </div>
              )}
              <div className="mt-4 flex items-center gap-2">
                <button
                  data-testid="wizard-sources-review-approve"
                  type="button"
                  disabled={reviewBusy}
                  onClick={saveAndApproveReview}
                  className="text-xs rounded-md bg-[var(--jc-blue)] text-white px-4 py-2 hover:opacity-90 disabled:opacity-50"
                >
                  {reviewBusy ? "Saving…" : "Save & Approve"}
                </button>
                <button
                  data-testid="wizard-sources-review-cancel"
                  type="button"
                  disabled={reviewBusy}
                  onClick={closeReview}
                  className="text-xs rounded-md border border-[var(--jc-border)] px-4 py-2 hover:bg-white disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};



const PricingProfileWizard = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [templatesMeta, setTemplatesMeta] = useState(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Wizard-local picks (persist only on the backend via load-template +
  // bulk-update-rates + mark-ready; we don't mirror state in Mongo until the
  // contractor presses a button).
  const [picks, setPicks] = useState({
    trades: [],
    systems: {}, // { tradeName: [systemName, ...] }
    // Feb 2026 UX pass — captured on new step 2, saved to the profile.
    pricingStyle: DEFAULT_PRICING_STYLE, // "unit" | "flat" | "labor_materials" | "hybrid"
    preferredUnit: "", // only meaningful when pricingStyle === "unit"
    laborRate: 65,
    markupPct: 25,
    taxRate: 8,
    depositPct: 50,
    confirmed: false,
  });

  // ----- Bootstrap -----
  useEffect(() => {
    const boot = async () => {
      try {
        const [pRes, tRes] = await Promise.all([
          api.get("/pricing-profile"),
          api.get("/pricing-profile/trade-templates"),
        ]);
        const p = pRes.data;
        if (p.setup_completed) {
          // Already set up → bounce to dashboard / editor. Avoids redirect
          // loops if the demo or a legacy contractor lands here directly.
          toast.message("Your pricing profile is already set up.");
          navigate("/app/pricing-profile", { replace: true });
          return;
        }
        setProfile(p);
        setTemplatesMeta(tRes.data);
        setPicks((prev) => ({
          ...prev,
          pricingStyle: _normalizeStyleForPicker(p.default_pricing_style),
          preferredUnit: p.preferred_unit_type || "",
          laborRate: p.labor_rate ?? 65,
          markupPct: p.markup_pct ?? 25,
          taxRate: p.tax_rate ?? 8,
          depositPct: p.deposit_pct ?? 50,
        }));
        setStep(Math.max(1, Math.min(p.setup_step || 1, STEPS.length)));
      } catch (err) {
        toast.error(t("onboarding.save_failed"));
      } finally {
        setLoading(false);
      }
    };
    boot();
  }, []);

  // Persist progress (best-effort; never blocks UI).
  const persistStep = async (nextStep) => {
    try {
      await api.patch("/pricing-profile/setup-progress", { step: nextStep });
    } catch {
      /* non-fatal */
    }
  };

  // Feb 2026 UX pass — save the contractor-declared pricing style + preferred
  // unit onto the profile. This runs on "Continue" from the pricing-style step
  // so the choice survives back/forward navigation and page reloads. It never
  // touches estimate math or Slice F calculation logic.
  const savePricingStyle = async () => {
    if (!profile) return;
    try {
      const payload = {
        ...profile,
        default_pricing_style: picks.pricingStyle || DEFAULT_PRICING_STYLE,
        preferred_unit_type:
          picks.pricingStyle === "unit" ? (picks.preferredUnit || "") : "",
      };
      delete payload.id;
      delete payload.user_id;
      delete payload.updated_at;
      const { data } = await api.put("/pricing-profile", payload);
      setProfile(data);
    } catch {
      /* non-fatal — user can retry via the Pricing Profile editor */
    }
  };

  // Feb 2026 — Learn From Approved Estimates inline sub-panel state.
  // Lives on Step 2 next to the pricing-style picker so contractors who
  // pick "Learn From My Approved Estimates" can review + confirm learned
  // candidates before continuing. The read call is deterministic and
  // tenant-scoped; the write call requires an explicit contractor click.
  const [learnState, setLearnState] = useState({
    loading: false,
    loaded: false,
    error: "",
    eligibleCount: 0,
    candidates: [],
    edits: {},          // { [candidate_id]: { price: number, accepted: bool } }
    savingAccept: false,
    savedResult: null,  // { written, skipped, errors }
  });

  const loadLearnCandidates = async () => {
    setLearnState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const { data } = await api.get("/pricing-profile/learn-from-history");
      const editsInit = {};
      for (const c of data.candidates || []) {
        editsInit[c.candidate_id] = {
          price: c.suggested_price,
          accepted: !c.already_in_profile, // pre-select non-duplicates
        };
      }
      setLearnState((s) => ({
        ...s,
        loading: false,
        loaded: true,
        eligibleCount: data.eligible_estimate_count || 0,
        candidates: data.candidates || [],
        edits: editsInit,
        savedResult: null,
      }));
    } catch (err) {
      setLearnState((s) => ({
        ...s,
        loading: false,
        loaded: true,
        error: "Could not load approved-estimate history right now.",
      }));
    }
  };

  const acceptLearnCandidates = async () => {
    const accepted = learnState.candidates
      .filter((c) => learnState.edits[c.candidate_id]?.accepted)
      .map((c) => ({
        candidate_id: c.candidate_id,
        trade: c.trade,
        system_type: c.system_type,
        unit: c.unit,
        selling_price_per_unit: Number(
          learnState.edits[c.candidate_id]?.price ?? c.suggested_price,
        ),
      }))
      .filter((row) => Number.isFinite(row.selling_price_per_unit) && row.selling_price_per_unit > 0);
    if (accepted.length === 0) {
      toast.message("Pick at least one candidate to accept.");
      return;
    }
    setLearnState((s) => ({ ...s, savingAccept: true }));
    try {
      const { data } = await api.post(
        "/pricing-profile/learn-from-history/accept",
        { accepted },
      );
      setLearnState((s) => ({
        ...s,
        savingAccept: false,
        savedResult: data,
      }));
      const w = (data.written || []).length;
      const sk = (data.skipped || []).length;
      const er = (data.errors || []).length;
      if (w > 0) toast.success(`${w} learned price${w === 1 ? "" : "s"} added.`);
      if (sk > 0) toast.message(`${sk} skipped (already in profile).`);
      if (er > 0) toast.error(`${er} could not be saved. See details below.`);
      // Refresh so already_in_profile flips true on newly-written candidates.
      await loadLearnCandidates();
    } catch (err) {
      setLearnState((s) => ({
        ...s,
        savingAccept: false,
        error: "Could not save learned prices right now.",
      }));
    }
  };

  useEffect(() => {
    if (picks.pricingStyle === "learn_from_history" && !learnState.loaded && !learnState.loading) {
      loadLearnCandidates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picks.pricingStyle]);

  // Set of step keys that are hidden for the current pricing style.
  // Labor rate is hidden entirely when the contractor picked unit or flat.
  const isLaborRelevant = LABOR_STYLES.has(picks.pricingStyle || DEFAULT_PRICING_STYLE);
  const visibleSteps = STEPS.filter((s) => (s.key === "labor" ? isLaborRelevant : true));
  const totalSteps = visibleSteps.length;
  const visibleStepNumber = (id) => {
    const idx = visibleSteps.findIndex((s) => s.id === id);
    return idx >= 0 ? idx + 1 : id;
  };
  // Find the next visible step id after `curId` (or wrap to last if none).
  const nextStepId = (curId) => {
    const idx = visibleSteps.findIndex((s) => s.id === curId);
    if (idx < 0 || idx === visibleSteps.length - 1) return curId;
    return visibleSteps[idx + 1].id;
  };
  const prevStepId = (curId) => {
    const idx = visibleSteps.findIndex((s) => s.id === curId);
    if (idx <= 0) return curId;
    return visibleSteps[idx - 1].id;
  };

  const go = async (nextId) => {
    const id = Math.min(Math.max(nextId, 1), STEPS.length);
    setStep(id);
    persistStep(id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ----- Derived data -----
  const supportedTrades = templatesMeta?.supported_trades || [];
  const tradeSystems = templatesMeta?.trade_systems || {};
  const rules = profile?.material_rules || [];

  const pricingTypeCounts = useMemo(() => {
    const out = { unit_priced: 0, fixed_price: 0, allowance: 0, sub_quote: 0, tm: 0 };
    for (const r of rules) {
      const pt = (r.pricing_type || "unit_priced").trim();
      if (pt in out) out[pt] += 1;
    }
    return out;
  }, [rules]);

  const loadedSystemsByTrade = useMemo(() => {
    const map = {};
    for (const r of rules) {
      const t = r.trade || "Other";
      const s = (r.system_type || "").trim();
      if (!s) continue;
      map[t] = map[t] || new Set();
      map[t].add(s);
    }
    return Object.fromEntries(Object.entries(map).map(([k, v]) => [k, Array.from(v)]));
  }, [rules]);

  // ----- Mutations -----
  const refetchProfile = async () => {
    const { data } = await api.get("/pricing-profile");
    setProfile(data);
  };

  const loadTemplate = async (trade, systemType) => {
    setBusy(true);
    try {
      const existing = (loadedSystemsByTrade[trade] || []).includes(systemType);
      if (existing) {
        if (!window.confirm(`Reload starter rules for ${trade} → ${systemType}? Your edits to that system's rules will be replaced.`)) {
          return;
        }
      }
      await api.post("/pricing-profile/load-template", {
        trade,
        system_type: systemType,
        mode: "replace",
      });
      await refetchProfile();
      toast.success(`Loaded ${trade} — ${systemType}`);
    } catch {
      toast.error(`Could not load ${trade} → ${systemType}`);
    } finally {
      setBusy(false);
    }
  };

  const applyLaborRate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pricing-profile/bulk-update-rates", {
        labor_rate: Number(picks.laborRate || 0),
        scope: "all",
      });
      setProfile(data.profile);
      toast.success(
        `Labor rate applied to ${data.labor_rate_applied_to} hourly rule${data.labor_rate_applied_to === 1 ? "" : "s"}`
      );
    } catch {
      toast.error("Could not apply labor rate");
    } finally {
      setBusy(false);
    }
  };

  const applyMarkup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pricing-profile/bulk-update-rates", {
        markup_pct: Number(picks.markupPct || 0),
        scope: "all",
      });
      setProfile(data.profile);
      toast.success(
        `Markup applied to ${data.markup_applied_to} rule${data.markup_applied_to === 1 ? "" : "s"}`
      );
    } catch {
      toast.error("Could not apply markup");
    } finally {
      setBusy(false);
    }
  };

  const saveTaxAndDeposit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...profile,
        tax_rate: Number(picks.taxRate || 0),
        deposit_pct: Number(picks.depositPct || 0),
      };
      // Strip non-input fields the PUT model doesn't accept.
      delete payload.id;
      delete payload.user_id;
      delete payload.updated_at;
      const { data } = await api.put("/pricing-profile", payload);
      setProfile(data);
      toast.success("Tax + deposit defaults saved");
    } catch {
      toast.error("Could not save tax + deposit");
    } finally {
      setBusy(false);
    }
  };

  const markReady = async () => {
    setBusy(true);
    try {
      await api.post("/pricing-profile/mark-ready");
      toast.success("Pricing profile ready. You can revise anytime in Pricing Profile.");
      navigate("/app/pricing-profile", { replace: true });
    } catch (err) {
      // iter54 correction · Backend now hard-rejects mark-ready with
      // HTTP 400 pricing_incomplete when no trade/system has priced
      // material_rules. Surface the contractor-facing reasons so the
      // user can act on them rather than seeing a generic error.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 400 && detail?.error === "pricing_incomplete") {
        const reasons = Array.isArray(detail.pricing_readiness_reasons)
          ? detail.pricing_readiness_reasons.slice(0, 3).join(" · ")
          : "";
        toast.error(reasons || "Add pricing for at least one trade/system before marking ready.");
      } else {
        toast.error("Could not mark profile ready");
      }
    } finally {
      setBusy(false);
    }
  };

  // ----- Loading guard -----
  if (loading) {
    return (
      <DashboardLayout title={t("onboarding.step_pricing_setup")} subtitle={t("onboarding.loading")}>
        <div className="flex items-center gap-2 text-sm text-[var(--jc-text-soft)]">
          <Loader2 className="animate-spin" size={16} /> Loading your profile…
        </div>
      </DashboardLayout>
    );
  }

  // ----- Step renderers -----
  const StepIndicator = (
    <div className="flex items-center gap-1.5 text-xs text-[var(--jc-text-soft)] mb-6" data-testid="wizard-stepper">
      {visibleSteps.map((s) => {
        const done = visibleStepNumber(s.id) < visibleStepNumber(step);
        const active = s.id === step;
        return (
          <div
            key={s.id}
            className={`flex items-center gap-1 px-2 py-1 rounded ${
              active
                ? "bg-[var(--jc-blue)]/10 text-[var(--jc-blue)] font-semibold border border-[var(--jc-blue)]/30"
                : done
                ? "text-[var(--jc-text)] border border-transparent"
                : "text-[var(--jc-text-soft)] border border-transparent"
            }`}
            data-testid={`wizard-step-pill-${s.key}`}
          >
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold border border-current">
              {done ? <Check size={10} /> : visibleStepNumber(s.id)}
            </span>
            <span className="hidden sm:inline">{s.label}</span>
          </div>
        );
      })}
    </div>
  );

  // Footer rendered via <WizardFooter /> (module-level component).

  return (
    <DashboardLayout
      title={t("onboarding.step_pricing_setup")}
      subtitle={t("onboarding.pricing_setup_body")}
    >
      <div className="max-w-3xl" data-testid="pricing-profile-wizard">
        {StepIndicator}

        {/* Step 1 — Welcome */}
        {step === 1 && (
          <section className="jc-card p-8" data-testid="wizard-step-welcome">
            <div className="flex items-start gap-4">
              <div className="shrink-0 w-12 h-12 rounded-md bg-[var(--jc-blue)]/10 text-[var(--jc-blue)] flex items-center justify-center">
                <Sparkles size={22} />
              </div>
              <div>
                <h2 className="font-display text-2xl tracking-tight">{t("onboarding.welcome_headline")}</h2>
                <p className="mt-2 text-[var(--jc-text)]">
                  This wizard helps you set up your <b>pricing profile</b> — the
                  numbers JobClose AI uses to draft estimates for you.
                </p>
                <p className="mt-4 text-[var(--jc-text)] leading-relaxed">
                  <b>Your pricing stays in your control.</b> JobClose AI supports unit pricing,
                  flat pricing, and labor/material pricing. You choose the mix that fits how you
                  actually run jobs — hourly labor is optional, not required.
                </p>
              </div>
            </div>
            <div
              data-testid="wizard-welcome-warning"
              className="mt-6 rounded-md border border-yellow-300 bg-yellow-50 px-4 py-3 flex items-start gap-3 text-sm text-yellow-900"
            >
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <div>
                <b>You must verify all pricing before sending estimates.</b> Starter labor rates, material costs, and markups are placeholders. Review every rule against your real supplier and labor numbers.
              </div>
            </div>

            {/* Phase 5B · Sources section — always visible. Contractor
                chooses how to build pricing: manually, from an existing
                pricing guide, or from previous estimates that Levi has
                already familiarized itself with. */}
            <PricingSourcesPanel />

            <WizardFooter busy={busy} onNext={() => go(2)} nextTestId="wizard-welcome-next-btn" />
          </section>
        )}

        {/* Step 2 — Pricing Style (Feb 2026 UX pass) */}
        {step === 2 && (
          <section className="jc-card p-8" data-testid="wizard-step-pricing-style">
            <div className="overline">Step {visibleStepNumber(2)} of {totalSteps}</div>
            <h2 className="font-display text-2xl tracking-tight mt-1">How do you usually price jobs?</h2>
            <p className="mt-2 text-sm text-[var(--jc-text-soft)]">
              Pick the way you usually price work. You can still price each estimate line by unit
              price, flat price, or labor + materials — this just tells us how to shape your setup.
            </p>
            <div
              role="radiogroup"
              aria-label="Default pricing style"
              className="mt-5 grid sm:grid-cols-2 gap-3"
            >
              {PRICING_STYLE_OPTIONS.map((opt) => {
                const active = (picks.pricingStyle || DEFAULT_PRICING_STYLE) === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    data-testid={`wizard-pricing-style-${opt.id}`}
                    onClick={() =>
                      setPicks((p) => ({
                        ...p,
                        pricingStyle: opt.id,
                        // Clear preferredUnit when leaving unit style so we don't
                        // persist a stale value on the profile.
                        preferredUnit: opt.id === "unit" ? p.preferredUnit : "",
                      }))
                    }
                    className={`text-left rounded-md border p-4 transition ${
                      active
                        ? "border-[var(--jc-blue)] bg-[var(--jc-blue)]/5 ring-2 ring-[var(--jc-blue)]/20"
                        : "border-[var(--jc-border-strong)] hover:border-[var(--jc-blue)]/50 hover:bg-[var(--jc-bg)]"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span
                        aria-hidden="true"
                        className={`mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 ${
                          active ? "border-[var(--jc-blue)] bg-[var(--jc-blue)]" : "border-[var(--jc-border-strong)]"
                        }`}
                      >
                        {active && <span className="block w-1.5 h-1.5 rounded-full bg-white m-auto mt-[3px]" />}
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-semibold text-sm text-[var(--jc-text)]">{opt.title}</div>
                          {opt.subtitle && (
                            <span
                              data-testid={`wizard-pricing-style-${opt.id}-badge`}
                              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-[var(--jc-blue)]/10 text-[var(--jc-blue)] border border-[var(--jc-blue)]/25"
                            >
                              {opt.subtitle}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-[var(--jc-text-soft)] mt-1 leading-relaxed">{opt.desc}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Unit follow-up — only when Unit / Production Pricing is selected. */}
            {picks.pricingStyle === "unit" && (
              <div className="mt-6 rounded-md border border-[var(--jc-border)] bg-[var(--jc-bg)]/40 p-4" data-testid="wizard-pricing-style-unit-followup">
                <div className="overline">Optional</div>
                <h3 className="font-display text-lg mt-1">What unit do you use most often?</h3>
                <p className="text-xs text-[var(--jc-text-soft)] mt-1 leading-relaxed">
                  Sets a default we can pre-fill on new estimate lines. You can still change the
                  unit on any individual line — nothing is locked. Roofers often pick <b>Roofing
                  square</b>, <b>Sq ft</b>, or <b>Flat / project</b>.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {PREFERRED_UNIT_OPTIONS.map((u) => {
                    const active = picks.preferredUnit === u;
                    const slug = u.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                    return (
                      <button
                        key={u}
                        type="button"
                        onClick={() => setPicks((p) => ({ ...p, preferredUnit: active ? "" : u }))}
                        data-testid={`wizard-preferred-unit-${slug}`}
                        aria-pressed={active}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                          active
                            ? "bg-[var(--jc-blue)] text-white border-[var(--jc-blue)]"
                            : "border-[var(--jc-border-strong)] text-[var(--jc-text)] hover:border-[var(--jc-blue)]/60"
                        }`}
                      >
                        {u}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Learn From Approved Estimates — inline sub-panel. Renders when
                the contractor picks "Learn From My Approved Estimates". Reads
                candidates deterministically from db.estimates via linked
                db.proposals in {approved, signed, fully_executed}. NO writes
                until the contractor explicitly clicks Accept. */}
            {picks.pricingStyle === "learn_from_history" && (
              <div
                className="mt-6 rounded-md border border-[var(--jc-border)] bg-[var(--jc-bg)]/40 p-4"
                data-testid="wizard-learn-from-history-panel"
              >
                <div className="overline">Levi learned pricing</div>
                <h3 className="font-display text-lg mt-1">Review learned pricing from your approved estimates</h3>
                <p className="text-xs text-[var(--jc-text-soft)] mt-1 leading-relaxed">
                  Levi looked at estimates connected to proposals you already
                  finalized — <b>approved</b>, <b>signed</b>, or
                  <b> fully executed</b>. Each suggestion is a starting point.
                  You can adjust the price, skip any row, or add all of them.
                  Nothing becomes an active pricing rule until you click
                  <b> Accept selected</b>.
                </p>

                {learnState.loading && (
                  <div
                    className="mt-4 flex items-center gap-2 text-sm text-[var(--jc-text-soft)]"
                    data-testid="wizard-learn-loading"
                  >
                    <Loader2 className="animate-spin" size={14} />
                    Reading your approved estimates…
                  </div>
                )}

                {learnState.error && !learnState.loading && (
                  <div
                    className="mt-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900"
                    data-testid="wizard-learn-error"
                  >
                    {learnState.error}
                  </div>
                )}

                {learnState.loaded && !learnState.loading && !learnState.error && learnState.candidates.length === 0 && (
                  <div
                    className="mt-4 rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900"
                    data-testid="wizard-learn-empty"
                  >
                    Levi didn't find approved estimates yet. Try
                    <b> All-in Price by Unit</b> or
                    <b> All-in Price by Job or System</b>, or pick
                    <b> Other / Help Me Set It Up</b> to configure pricing
                    manually. You can come back here after you close your
                    first few jobs.
                  </div>
                )}

                {learnState.loaded && !learnState.loading && learnState.candidates.length > 0 && (
                  <>
                    <div className="mt-4 text-xs text-[var(--jc-text-soft)]" data-testid="wizard-learn-summary">
                      Based on <b>{learnState.eligibleCount}</b> approved estimate{learnState.eligibleCount === 1 ? "" : "s"}.
                      Suggested prices are the median of your own past prices — you can edit any row.
                    </div>
                    <ul
                      className="mt-3 divide-y divide-[var(--jc-border)] border border-[var(--jc-border)] rounded-md bg-white"
                      data-testid="wizard-learn-candidates"
                    >
                      {learnState.candidates.map((c) => {
                        const editRow = learnState.edits[c.candidate_id] || {};
                        const priceVal = editRow.price ?? c.suggested_price;
                        const accepted = !!editRow.accepted;
                        return (
                          <li
                            key={c.candidate_id}
                            className="p-3 flex flex-wrap sm:flex-nowrap items-center gap-3"
                            data-testid={`wizard-learn-row-${c.candidate_id}`}
                          >
                            <label className="flex items-center gap-2 text-sm min-w-0 flex-1">
                              <input
                                type="checkbox"
                                checked={accepted}
                                onChange={(e) =>
                                  setLearnState((s) => ({
                                    ...s,
                                    edits: {
                                      ...s.edits,
                                      [c.candidate_id]: {
                                        ...(s.edits[c.candidate_id] || { price: c.suggested_price }),
                                        accepted: e.target.checked,
                                      },
                                    },
                                  }))
                                }
                                disabled={c.already_in_profile}
                                data-testid={`wizard-learn-accept-${c.candidate_id}`}
                                className="h-4 w-4"
                              />
                              <span className="min-w-0">
                                <div className="font-semibold text-sm text-[var(--jc-text)] truncate">
                                  {c.trade} · {c.system_type}
                                </div>
                                <div className="text-xs text-[var(--jc-text-soft)]">
                                  {c.sample_count} past estimate{c.sample_count === 1 ? "" : "s"}
                                  {" · "}range ${c.min_price.toFixed(2)} – ${c.max_price.toFixed(2)}
                                  {c.already_in_profile && (
                                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-800 border border-green-300">
                                      Already in profile
                                    </span>
                                  )}
                                </div>
                              </span>
                            </label>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="text-xs text-[var(--jc-text-soft)]">$</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={priceVal}
                                onChange={(e) =>
                                  setLearnState((s) => ({
                                    ...s,
                                    edits: {
                                      ...s.edits,
                                      [c.candidate_id]: {
                                        ...(s.edits[c.candidate_id] || { accepted: true }),
                                        price: e.target.value === "" ? "" : Number(e.target.value),
                                      },
                                    },
                                  }))
                                }
                                disabled={c.already_in_profile}
                                data-testid={`wizard-learn-price-${c.candidate_id}`}
                                className="w-24 px-2 py-1 text-sm border border-[var(--jc-border-strong)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--jc-blue)] disabled:bg-[var(--jc-bg)] disabled:opacity-60"
                              />
                              <span className="text-xs text-[var(--jc-text-soft)] font-mono-jc">/ {c.unit}</span>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                    <div className="mt-4 flex items-center justify-between">
                      <button
                        type="button"
                        onClick={loadLearnCandidates}
                        disabled={learnState.loading || learnState.savingAccept}
                        data-testid="wizard-learn-refresh-btn"
                        className="text-xs font-semibold text-[var(--jc-blue)] hover:underline disabled:opacity-40"
                      >
                        Refresh
                      </button>
                      <button
                        type="button"
                        onClick={acceptLearnCandidates}
                        disabled={learnState.savingAccept}
                        data-testid="wizard-learn-accept-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60"
                      >
                        {learnState.savingAccept ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />}
                        Accept selected
                      </button>
                    </div>
                    {learnState.savedResult && (
                      <div
                        className="mt-3 rounded-md border border-[var(--jc-border)] bg-white p-3 text-xs text-[var(--jc-text-soft)]"
                        data-testid="wizard-learn-result"
                      >
                        Added <b>{(learnState.savedResult.written || []).length}</b> new rule{(learnState.savedResult.written || []).length === 1 ? "" : "s"}
                        {(learnState.savedResult.skipped || []).length > 0 && (
                          <> · <b>{(learnState.savedResult.skipped || []).length}</b> already in profile</>
                        )}
                        {(learnState.savedResult.errors || []).length > 0 && (
                          <> · <b className="text-red-700">{(learnState.savedResult.errors || []).length}</b> could not be saved</>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <WizardFooter
              busy={busy}
              onBack={() => go(1)}
              onNext={async () => {
                await savePricingStyle();
                go(nextStepId(2));
              }}
              nextTestId="wizard-pricing-style-next-btn"
            />
          </section>
        )}

        {/* Step 3 — Select Trades */}
        {step === 3 && (
          <section className="jc-card p-8" data-testid="wizard-step-trades">
            <div className="overline">Step {visibleStepNumber(3)} of {totalSteps}</div>
            <h2 className="font-display text-2xl tracking-tight mt-1">Which trades do you work?</h2>
            <p className="mt-2 text-sm text-[var(--jc-text-soft)]">
              Pick all that apply. You can add more later from your Pricing Profile.
            </p>
            <div className="mt-5 grid sm:grid-cols-2 gap-2">
              {supportedTrades.map((t) => {
                const selected = picks.trades.includes(t);
                return (
                  <button
                    key={t}
                    onClick={() =>
                      setPicks((p) => ({
                        ...p,
                        trades: selected ? p.trades.filter((x) => x !== t) : [...p.trades, t],
                        systems: selected ? { ...p.systems, [t]: [] } : p.systems,
                      }))
                    }
                    data-testid={`wizard-trade-${t.toLowerCase()}`}
                    className={`px-3 py-2.5 rounded-md text-sm font-semibold border text-left transition ${
                      selected
                        ? "bg-[var(--jc-blue)]/10 border-[var(--jc-blue)] text-[var(--jc-blue)]"
                        : "border-[var(--jc-border)] hover:border-[var(--jc-border-strong)]"
                    }`}
                  >
                    <span className="inline-flex items-center gap-2">
                      {selected ? <Check size={14} /> : null}
                      {t}
                    </span>
                    <span className="block text-[11px] text-[var(--jc-text-soft)] font-normal mt-0.5">
                      {tradeSystems[t]?.systems?.length || 0} system type
                      {(tradeSystems[t]?.systems?.length || 0) === 1 ? "" : "s"} available
                    </span>
                  </button>
                );
              })}
            </div>
            <WizardFooter busy={busy}
              onBack={() => go(prevStepId(3))}
              onNext={() => go(nextStepId(3))}
              nextDisabled={picks.trades.length === 0}
              nextTestId="wizard-trades-next-btn"
            />
          </section>
        )}

        {/* Step 4 — Select System Types */}
        {step === 4 && (
          <section className="jc-card p-8" data-testid="wizard-step-systems">
            <div className="overline">Step {visibleStepNumber(4)} of {totalSteps}</div>
            <h2 className="font-display text-2xl tracking-tight mt-1">Which system types under each trade?</h2>
            <p className="mt-2 text-sm text-[var(--jc-text-soft)]">
              Pick the system types you actually estimate. Each loads a different set of starter rules.
            </p>
            <div className="mt-5 space-y-4">
              {picks.trades.map((t) => {
                const systems = tradeSystems[t]?.systems || [];
                const selected = picks.systems[t] || [];
                return (
                  <div key={t} className="rounded-md border border-[var(--jc-border)] p-4">
                    <div className="font-semibold text-sm mb-2">{t}</div>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {systems.map((s) => {
                        const isSel = selected.includes(s.name);
                        const slug = s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                        return (
                          <button
                            key={s.name}
                            onClick={() =>
                              setPicks((p) => {
                                const cur = p.systems[t] || [];
                                const next = isSel ? cur.filter((x) => x !== s.name) : [...cur, s.name];
                                return { ...p, systems: { ...p.systems, [t]: next } };
                              })
                            }
                            data-testid={`wizard-system-${t.toLowerCase()}-${slug}`}
                            className={`px-3 py-2 rounded text-xs font-semibold border text-left transition ${
                              isSel
                                ? "bg-[var(--jc-blue)]/10 border-[var(--jc-blue)] text-[var(--jc-blue)]"
                                : "border-[var(--jc-border)] hover:border-[var(--jc-border-strong)]"
                            }`}
                          >
                            <span className="inline-flex items-center gap-1.5">
                              {isSel ? <Check size={12} /> : null}
                              {s.name}
                            </span>
                            <span className="block text-[10px] text-[var(--jc-text-soft)] font-normal mt-0.5">
                              {s.rule_count} starter rule{s.rule_count === 1 ? "" : "s"}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <WizardFooter busy={busy}
              onBack={() => go(prevStepId(4))}
              onNext={() => go(nextStepId(4))}
              nextDisabled={!picks.trades.some((t) => (picks.systems[t] || []).length > 0)}
              nextTestId="wizard-systems-next-btn"
            />
          </section>
        )}

        {/* Step 5 — Load Starter Templates */}
        {step === 5 && (
          <section className="jc-card p-8" data-testid="wizard-step-templates">
            <div className="overline">Step {visibleStepNumber(5)} of {totalSteps}</div>
            <h2 className="font-display text-2xl tracking-tight mt-1">Load starter templates</h2>
            <p className="mt-2 text-sm text-[var(--jc-text-soft)]">
              Click each system to load its starter rules. You can edit every rule later in the
              full Pricing Profile editor.
            </p>
            <div
              data-testid="wizard-templates-warning"
              className="mt-4 rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-xs text-yellow-900"
            >
              Loading replaces any previous rules for the same trade + system type. Other trades / system types are not affected.
            </div>
            <div className="mt-5 space-y-3">
              {picks.trades.map((t) =>
                (picks.systems[t] || []).map((s) => {
                  const slug = s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                  const ruleCount =
                    (tradeSystems[t]?.systems || []).find((x) => x.name === s)?.rule_count || 0;
                  const isLoaded = (loadedSystemsByTrade[t] || []).includes(s);
                  return (
                    <div
                      key={`${t}::${s}`}
                      className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-md border border-[var(--jc-border)]"
                      data-testid={`wizard-load-row-${t.toLowerCase()}-${slug}`}
                    >
                      <div className="text-sm">
                        <div className="font-semibold">{t} — {s}</div>
                        <div className="text-[11px] text-[var(--jc-text-soft)]">
                          {ruleCount} starter rule{ruleCount === 1 ? "" : "s"}
                          {isLoaded ? " · loaded" : ""}
                        </div>
                      </div>
                      <button
                        onClick={() => loadTemplate(t, s)}
                        disabled={busy}
                        data-testid={`wizard-load-btn-${t.toLowerCase()}-${slug}`}
                        className={`px-3 py-1.5 text-xs font-semibold rounded ${
                          isLoaded
                            ? "border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
                            : "jc-btn-primary"
                        }`}
                      >
                        {isLoaded ? "Reload" : "Load starter"}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
            <WizardFooter busy={busy}
              onBack={() => go(prevStepId(5))}
              onNext={() => go(nextStepId(5))}
              nextDisabled={rules.length === 0}
              nextTestId="wizard-templates-next-btn"
            />
          </section>
        )}

        {/* Step 6 — Labor Rate (only shown when style is labor_materials / hybrid) */}
        {step === 6 && isLaborRelevant && (
          <section className="jc-card p-8" data-testid="wizard-step-labor">
            <div className="overline">Step {visibleStepNumber(6)} of {totalSteps} · Optional</div>
            <h2 className="font-display text-2xl tracking-tight mt-1">Optional labor rate</h2>
            <p className="mt-2 text-sm text-[var(--jc-text)]">
              Use this only for labor/hourly line items. You can still price estimates by unit price,
              flat price, or labor/materials — this is not required to finish setup.
            </p>
            <p className="mt-1 text-xs text-[var(--jc-text-soft)]">
              Applied only to rules billed <b>per hour</b>. Per-square, per-unit, and per-job rules
              keep the labor amounts they shipped with — adjust those individually in the full
              Pricing Profile editor.
            </p>
            <div className="mt-5 flex items-center gap-3 max-w-xs">
              <span className="text-sm text-[var(--jc-text-soft)]">$</span>
              <input
                type="number"
                step="any"
                value={picks.laborRate}
                onChange={(e) => setPicks((p) => ({ ...p, laborRate: e.target.value }))}
                data-testid="wizard-labor-rate-input"
                className="flex-1 px-3 py-2 rounded-md border border-[var(--jc-border-strong)] font-mono-jc text-right"
              />
              <span className="text-sm text-[var(--jc-text-soft)]">/ hr</span>
            </div>
            <button
              onClick={applyLaborRate}
              disabled={busy}
              data-testid="wizard-labor-apply-btn"
              className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md jc-btn-primary disabled:opacity-60"
            >
              {busy ? <Loader2 className="animate-spin" size={12} /> : null}
              Apply to hourly labor rules
            </button>
            <WizardFooter busy={busy} onBack={() => go(prevStepId(6))} onNext={() => go(nextStepId(6))} nextTestId="wizard-labor-next-btn" />
          </section>
        )}

        {/* Step 7 — Markup */}
        {step === 7 && (
          <section className="jc-card p-8" data-testid="wizard-step-markup">
            <div className="overline">Step {visibleStepNumber(7)} of {totalSteps}</div>
            <h2 className="font-display text-2xl tracking-tight mt-1">Set your default markup</h2>
            <p className="mt-2 text-sm text-[var(--jc-text)]">
              What markup percentage should JobClose AI default to on every starter rule?
            </p>
            <p className="mt-1 text-xs text-[var(--jc-text-soft)]">
              You can override markup on any individual rule later. Overhead and profit-margin
              settings remain available in the full Pricing Profile editor.
            </p>
            <div className="mt-5 flex items-center gap-3 max-w-xs">
              <input
                type="number"
                step="any"
                value={picks.markupPct}
                onChange={(e) => setPicks((p) => ({ ...p, markupPct: e.target.value }))}
                data-testid="wizard-markup-input"
                className="flex-1 px-3 py-2 rounded-md border border-[var(--jc-border-strong)] font-mono-jc text-right"
              />
              <span className="text-sm text-[var(--jc-text-soft)]">%</span>
            </div>
            <button
              onClick={applyMarkup}
              disabled={busy}
              data-testid="wizard-markup-apply-btn"
              className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md jc-btn-primary disabled:opacity-60"
            >
              {busy ? <Loader2 className="animate-spin" size={12} /> : null}
              Apply markup to all loaded rules
            </button>
            <WizardFooter busy={busy} onBack={() => go(prevStepId(7))} onNext={() => go(nextStepId(7))} nextTestId="wizard-markup-next-btn" />
          </section>
        )}

        {/* Step 8 — Tax + Deposit */}
        {step === 8 && (
          <section className="jc-card p-8" data-testid="wizard-step-tax-deposit">
            <div className="overline">Step {visibleStepNumber(8)} of {totalSteps}</div>
            <h2 className="font-display text-2xl tracking-tight mt-1">Tax + deposit defaults</h2>
            <p className="mt-2 text-sm text-[var(--jc-text)]">
              These become your defaults on new estimates. You can still override either per
              estimate when needed.
            </p>
            <div className="mt-5 grid sm:grid-cols-2 gap-4 max-w-md">
              <div>
                <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1">Sales tax rate</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    step="any"
                    value={picks.taxRate}
                    onChange={(e) => setPicks((p) => ({ ...p, taxRate: e.target.value }))}
                    data-testid="wizard-tax-input"
                    className="flex-1 px-3 py-2 rounded-md border border-[var(--jc-border-strong)] font-mono-jc text-right"
                  />
                  <span className="text-sm text-[var(--jc-text-soft)]">%</span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[var(--jc-text-soft)] mb-1">Default deposit</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    step="any"
                    value={picks.depositPct}
                    onChange={(e) => setPicks((p) => ({ ...p, depositPct: e.target.value }))}
                    data-testid="wizard-deposit-input"
                    className="flex-1 px-3 py-2 rounded-md border border-[var(--jc-border-strong)] font-mono-jc text-right"
                  />
                  <span className="text-sm text-[var(--jc-text-soft)]">%</span>
                </div>
              </div>
            </div>
            <button
              onClick={saveTaxAndDeposit}
              disabled={busy}
              data-testid="wizard-tax-deposit-save-btn"
              className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md jc-btn-primary disabled:opacity-60"
            >
              {busy ? <Loader2 className="animate-spin" size={12} /> : null}
              <Wallet size={12} /> Save tax + deposit defaults
            </button>
            <WizardFooter busy={busy} onBack={() => go(prevStepId(8))} onNext={() => go(nextStepId(8))} nextTestId="wizard-tax-deposit-next-btn" />
          </section>
        )}

        {/* Step 9 — Review */}
        {step === 9 && (
          <section className="jc-card p-8" data-testid="wizard-step-review">
            <div className="overline">Step {visibleStepNumber(9)} of {totalSteps}</div>
            <h2 className="font-display text-2xl tracking-tight mt-1">Review your loaded rules</h2>
            <p className="mt-2 text-sm text-[var(--jc-text-soft)]">
              Summary only. Per-rule editing lives in the full Pricing Profile editor.
            </p>

            <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-5 gap-2" data-testid="wizard-pricing-type-summary">
              {Object.entries(PRICING_TYPE_LABELS).map(([key, label]) => (
                <div
                  key={key}
                  className="rounded-md border border-[var(--jc-border)] px-3 py-2"
                  data-testid={`wizard-pt-card-${key}`}
                >
                  <div className="text-[10px] uppercase tracking-wider text-[var(--jc-text-soft)] font-semibold">{label}</div>
                  <div className="font-display text-2xl mt-1" data-testid={`wizard-pt-count-${key}`}>
                    {pricingTypeCounts[key] || 0}
                  </div>
                  <div className="text-[10px] text-[var(--jc-text-soft)]">rule{(pricingTypeCounts[key] || 0) === 1 ? "" : "s"}</div>
                </div>
              ))}
            </div>

            <div className="mt-5 rounded-md border border-[var(--jc-border)] p-4">
              <div className="text-xs font-semibold text-[var(--jc-text)] mb-2">By trade + system</div>
              {Object.keys(loadedSystemsByTrade).length === 0 ? (
                <div className="text-xs text-[var(--jc-text-soft)]">No rules loaded yet.</div>
              ) : (
                <ul className="text-xs text-[var(--jc-text)] space-y-1">
                  {Object.entries(loadedSystemsByTrade).map(([t, sys]) => (
                    <li key={t}>
                      <b>{t}</b>: {sys.join(", ")}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <a
              href="/app/pricing-profile"
              target="_blank"
              rel="noreferrer"
              data-testid="wizard-open-editor-link"
              className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--jc-blue)] hover:underline"
            >
              <ExternalLink size={12} /> Open full Pricing Profile editor in a new tab
            </a>

            <WizardFooter busy={busy} onBack={() => go(prevStepId(9))} onNext={() => go(nextStepId(9))} nextTestId="wizard-review-next-btn" />
          </section>
        )}

        {/* Step 10 — Confirm + Mark Ready */}
        {step === 10 && (
          <section className="jc-card p-8" data-testid="wizard-step-confirm">
            <div className="overline">Step {visibleStepNumber(10)} of {totalSteps}</div>
            <h2 className="font-display text-2xl tracking-tight mt-1">Confirm + finish setup</h2>
            <div className="mt-4 rounded-md border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-900">
              <div className="flex items-start gap-2">
                <ShieldCheck size={16} className="mt-0.5 shrink-0" />
                <div>
                  <b>You own your pricing.</b> Starter labor rates, material costs, markups, and
                  defaults are placeholders — review every rule against your supplier and labor numbers
                  before sending estimates to customers.
                </div>
              </div>
            </div>
            <label className="mt-5 flex items-start gap-2 text-sm text-[var(--jc-text)]" data-testid="wizard-confirm-checkbox-wrap">
              <input
                type="checkbox"
                checked={picks.confirmed}
                onChange={(e) => setPicks((p) => ({ ...p, confirmed: e.target.checked }))}
                data-testid="wizard-confirm-checkbox"
                className="mt-1"
              />
              <span>
                I confirm I have reviewed these starter numbers and understand I must verify my
                real pricing before sending anything to a customer.
              </span>
            </label>
            <div className="mt-6 flex items-center justify-between">
              <button
                onClick={() => go(prevStepId(10))}
                disabled={busy}
                data-testid="wizard-confirm-back-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)] disabled:opacity-40"
              >
                <ArrowLeft size={14} /> Back
              </button>
              <button
                onClick={markReady}
                disabled={!picks.confirmed || busy}
                data-testid="wizard-mark-ready-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60"
              >
                {busy ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />}
                Finish setup
              </button>
            </div>
          </section>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PricingProfileWizard;
